/**
 * Baixa um gráfico como imagem PNG (para mandar no WhatsApp).
 * Como funciona: desenha o gráfico fora da tela, já com título e rodapé
 * (props `cabecalho`) e nas cores claras, transforma o SVG em imagem, pinta
 * num <canvas> em tamanho dobrado (fica nítido no celular) e baixa o arquivo.
 * Fotos e logo: um SVG virando imagem não busca nada fora dele, então cada
 * <image> é trocada pela própria imagem embutida (data:) antes; se uma foto
 * não carregar, ela sai do PNG e ficam as iniciais.
 *
 * Lê: o elemento React do gráfico.
 * Usado por: componentes/CartaoGrafico.tsx.
 */
import type { ReactElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

/** Largura da imagem: 1080 px é o tamanho que o WhatsApp e o Instagram usam. */
export const LARGURA_IMAGEM = 1080;

function carregarImagem(url: string): Promise<HTMLImageElement> {
  return new Promise((resolver, rejeitar) => {
    const imagem = new Image();
    imagem.onload = () => resolver(imagem);
    imagem.onerror = () => rejeitar(new Error('não consegui desenhar a imagem do gráfico'));
    imagem.src = url;
  });
}

/** Endereço (/api/imagens.php?...) → a imagem embutida em "data:...", para ir dentro do SVG. */
async function embutir(endereco: string): Promise<string> {
  const resposta = await fetch(endereco, { credentials: 'same-origin' });
  if (!resposta.ok) throw new Error(`imagem ${resposta.status}`);
  const conteudo = await resposta.blob();
  return new Promise((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result));
    leitor.onerror = () => rejeitar(new Error('não consegui ler a imagem'));
    leitor.readAsDataURL(conteudo);
  });
}

async function embutirImagens(svg: SVGSVGElement): Promise<void> {
  const cache = new Map<string, Promise<string>>();
  await Promise.all(
    [...svg.querySelectorAll('image')].map(async (imagem) => {
      const endereco = imagem.getAttribute('href') ?? '';
      if (endereco === '' || endereco.startsWith('data:')) return;
      if (!cache.has(endereco)) cache.set(endereco, embutir(endereco));
      try {
        imagem.setAttribute('href', await (cache.get(endereco) as Promise<string>));
      } catch {
        imagem.remove();
      }
    }),
  );
}

export async function baixarComoPng(grafico: ReactElement, nomeArquivo: string): Promise<void> {
  const hospedeiro = document.createElement('div');
  hospedeiro.style.position = 'fixed';
  hospedeiro.style.left = '-20000px';
  hospedeiro.style.top = '0';
  document.body.append(hospedeiro);
  const raiz = createRoot(hospedeiro);
  try {
    flushSync(() => raiz.render(grafico));
    const svg = hospedeiro.querySelector('svg');
    if (!svg) throw new Error('o gráfico não gerou um SVG');
    await embutirImagens(svg);
    const largura = Number(svg.getAttribute('width'));
    const altura = Number(svg.getAttribute('height'));
    const urlSvg = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' }));
    try {
      const imagem = await carregarImagem(urlSvg);
      const escala = 2;
      const tela = document.createElement('canvas');
      tela.width = largura * escala;
      tela.height = altura * escala;
      const contexto = tela.getContext('2d');
      if (!contexto) throw new Error('o navegador não deixou criar a imagem');
      contexto.scale(escala, escala);
      contexto.drawImage(imagem, 0, 0, largura, altura);
      const png = await new Promise<Blob | null>((resolver) => tela.toBlob(resolver, 'image/png'));
      if (!png) throw new Error('não consegui gerar o PNG');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(png);
      link.download = nomeArquivo;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
    } finally {
      URL.revokeObjectURL(urlSvg);
    }
  } finally {
    raiz.unmount();
    hospedeiro.remove();
  }
}
